import { Component, DestroyRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatAnchor, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { InstallBannerComponent } from '../shared/install-banner/install-banner.component';
import { PRIVACY_POLICY_URL } from '../shared/legal-urls';
import { OPENED_BY_LINK } from '../deep-link';
import { endedLongAgo } from '../checkin/classes/saved-session';
// Single-sourced from package.json (the deploy bump updates it) — same pattern as
// the settings screen's version footer.
import { version as appVersion } from '../../../package.json';

const WEBSITE_URL = 'https://realguest.it';
// Bundled locally (assets/img/logo.png) rather than pulled from the CDN: the
// native app must show its brand offline / on a flaky connection — a remote logo
// "sometimes doesn't load" on the phone. Same wordmark, now reliable.
const LOGO_URL = 'assets/img/logo.png';

interface SavedEvent {
  id: string;
  title: string;
  pin: string;
  toDate?: string;
}

/**
 * The app's landing page, shown at '' when opened with no event context (the
 * check-in flow is otherwise entered by deep link / QR). It gives the app a real
 * home instead of the previous blank screen: brand + version + website, a primary
 * "scan the event QR" action, an optional "resume last event" affordance when a
 * session is still saved, and — in a web browser only — a "get the app" banner.
 */
@Component({
  selector: 'nbd-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [
    MatButton,
    MatAnchor,
    MatIcon,
    TranslatePipe,
    LanguageSelectorComponent,
    InstallBannerComponent,
  ],
})
export class HomeComponent {
  private readonly router = inject(Router);
  private readonly openedByLink = inject(OPENED_BY_LINK);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly appVersion = appVersion;
  protected readonly logoUrl = LOGO_URL;
  protected readonly websiteUrl = WEBSITE_URL;
  /** Store-mandated in-app privacy link — see [[PRIVACY_POLICY_URL]]. */
  protected readonly privacyUrl = PRIVACY_POLICY_URL;

  /**
   * The last event still saved in localStorage, if any. Home resumes it by itself
   * when it is the first screen of a launch (autoResume); otherwise it is offered
   * behind the "Resume" button.
   */
  protected readonly savedEvent = this.readSavedEvent();

  constructor() {
    this.autoResume();
  }

  scan(): void {
    this.router.navigate(['/scan-event']);
  }

  enterCode(): void {
    this.router.navigate(['/enter-code']);
  }

  resume(): void {
    const ev = this.savedEvent;
    if (ev) {
      // Re-enter through the existing deep-link route so the PIN is re-validated
      // and a fresh token is issued (the stored one may have expired). `resume`
      // lets that screen skip the group picker when the group picked in this
      // event is still valid: resuming should put the door back where it was.
      this.router.navigate(['/checkin', ev.id], {
        queryParams: { pin: ev.pin, resume: '1' },
      });
    }
  }

  /**
   * Go straight back into the saved event when Home is the FIRST screen of this
   * launch.
   *
   * The native app always boots at '': Capacitor loads the WebView at its root,
   * never at the route it was on. So whenever iOS discards the WebView (often, on a
   * phone with the camera running) the door came back on Home looking logged out,
   * and staff re-scanned the event QR. At the 2026-09-12 event one iPhone did that
   * at least 11 times in 96 minutes, with the saved session intact throughout: only
   * the route had been lost. (On the web a reload keeps its URL, so this runs only
   * when the site is opened at its root, e.g. from an icon on the home screen.)
   *
   * First screen only: `router.navigated` stays false until the initial navigation
   * ends, i.e. while it is activating this component. A later visit to Home was a
   * choice (backing out of an event) or a bounce (the PIN no longer opens it, and
   * load-event sends the operator here), and must stay put. That is also what keeps
   * an ended event from looping.
   *
   * Not an event that ended over a day ago (endedLongAgo): the server can only refuse
   * it, and every refusal is charged to the venue's failed-PIN budget. The Resume
   * button still offers it.
   *
   * A link that opened the app wins, since it names the event to open: wait for the
   * launch URL to settle, then resume only if no link came and Home is still up.
   */
  private autoResume(): void {
    if (
      this.router.navigated ||
      !this.savedEvent ||
      endedLongAgo(this.savedEvent.toDate, Date.now())
    ) {
      return;
    }
    let gone = false;
    this.destroyRef.onDestroy(() => (gone = true));
    this.openedByLink().then((byLink) => {
      if (!byLink && !gone) {
        this.resume();
      }
    });
  }

  private readSavedEvent(): SavedEvent | null {
    const pin = localStorage.getItem('RGCheckInPIN');
    if (!pin) {
      return null;
    }
    try {
      const ev = JSON.parse(localStorage.getItem('RGCheckInEvent') ?? 'null');
      return ev?.id ? { id: ev.id, title: ev.title ?? '', pin, toDate: ev.toDate } : null;
    } catch {
      return null;
    }
  }
}
